import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Avatar,
  TextField,
  Paper,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import axios from "axios";
import api from "../../api/endpoints";
import VideoRatingModal from "./VideoRatingModal";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export type Comment = {
  comment_id?: number;
  user_id: number;
  first_name?: string;
  vid_comment?: string;
  updated_on?: string;
};

// --- Video Player ---
const VideoPlayer: React.FC<{ videoUrl: string }> = ({ videoUrl }) => {
  const [playTime, setPlayTime] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [open, setOpen] = useState(false);

  // Inline extraction + validation of YouTube videoId from provided URL.
  const getVideoId = (raw: string) => {
    try {
      // Normalize input so that URL parsing works even if protocol is omitted.
      const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
      const u = new URL(normalized);
      let id = "";
      const hostname = u.hostname.toLowerCase();

      if (hostname === "youtu.be") {
        // Short link variant: https://youtu.be/yiFxruo4Stc
        id = u.pathname.slice(1);
      } else if (
        hostname === "www.youtube.com" ||
        hostname === "youtube.com" ||
        hostname.endsWith(".youtube.com")
      ) {
        // Standard watch URL: https://www.youtube.com/watch?v=yiFxruo4Stc
        id = u.searchParams.get("v") || "";
      }

      // Basic validation: YouTube video IDs are 11 characters consisting of A-Z a-z 0-9 _-
      // if (/^[a-zA-Z0-9_-]{11}$/.test(id)) {
      //   return id;
      // }
      // return "";
      return id;
    } catch {
      return "";
    }
  };

  const videoId = getVideoId(videoUrl);

  useEffect(() => {
    if (!videoId) {
      setHasError(true);
      return;
    }
    setHasError(false);

    const createPlayer = () => {
      playerRef.current = new window.YT.Player("youtube-player-container", {
        videoId,
        playerVars: {
          autoplay: 1, // start paused; user can play
          mute: 0,
          rel: 0,
        },
        events: {
          onReady: (event: any) => {
            const iframe = event.target.getIframe();
            console.log("Embed iframe src:", iframe.src);
          },
          onStateChange: onPlayerStateChange,
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = () => {
        createPlayer();
      };
    }

    return () => {
      // clear global hook to avoid stale reference
      window.onYouTubeIframeAPIReady = () => {};
    };
  }, [videoId]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoId]);

  const onPlayerStateChange = (event: any) => {
    const YT = window.YT;
    if (!YT) return;

    if (event.data === YT.PlayerState.PLAYING) {
      setIsPlaying(true);
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          setPlayTime((t) => t + 1);
        }, 1000);
      }
    } else {
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const playVideo = () => {
    if (playerRef.current?.playVideo) {
      playerRef.current.playVideo();
    }
  };

  const pauseVideo = () => {
    if (playerRef.current?.pauseVideo) {
      playerRef.current.pauseVideo();
    }
  };

  const handleSamplePPT = () => {
    try {
      const url = "https://jgatenext.com/cdn/NCG-sample-support.pptx"; // replace with actual URL
      const newWin = window.open(url, "_blank", "noopener,noreferrer");
      if (!newWin) throw new Error("Popup blocked by browser");
    } catch (e) {
      console.error("Open PPT error:", e);
      //   setError("Unable to open sample PPT.");
    }
  };

  const handleModel = (ratings: { question: string; rating: number }[]) => {
    console.log("Submitted ratings:", ratings);
    setOpen(true);
  };

  const formatTime = (s: number) => {
    if (isNaN(s) || s < 0) return "00:00";
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(
      s % 60
    ).padStart(2, "0")}`;
  };

  if (hasError) {
    return (
      <Box>
        <Box
          sx={{
            aspectRatio: "16/9",
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 3,
            bgcolor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="white">
            Invalid YouTube link: <br />
            <code>{videoUrl}</code>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <VideoRatingModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleModel}
      />
      <Box
        id="youtube-player-container"
        sx={{
          aspectRatio: "16/9",
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 3,
        }}
      />
      <Box mt={2} textAlign="center">
        <Typography variant="subtitle1" gutterBottom>
          You have watched: <strong>{formatTime(playTime)}</strong>
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            onClick={playVideo}
            variant="contained"
            disabled={isPlaying}
            sx={{ minWidth: 100 }}
          >
            ▶️ Play
          </Button>
          <Button
            onClick={pauseVideo}
            variant="outlined"
            disabled={!isPlaying}
            sx={{ minWidth: 100 }}
          >
            ⏸️ Pause
          </Button>
          <Button
            onClick={handleSamplePPT}
            variant="outlined"
            // disabled={!ready}
            sx={{ minWidth: 100 }}
          >
            Sample PPT
          </Button>

          <Button
            onClick={() => setOpen(true)}
            variant="outlined"
            // disabled={!ready}
            sx={{ minWidth: 100 }}
          >
            Rate Video
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

// --- Like Button ---
interface LikeButtonProps {
  likes: number;
  isInitiallyLiked?: boolean;
  onToggle: (newLiked: boolean) => void;
}
// --- Like Button ---
export const LikeButton: React.FC<LikeButtonProps> = ({
  likes,
  isInitiallyLiked = false,
  onToggle,
}) => {
  const [count, setCount] = useState(likes);
  const [isLiked, setIsLiked] = useState(isInitiallyLiked);
  const togglingRef = useRef(false);

  const toggleLike = async () => {
    if (togglingRef.current) return; // prevent double-click storms
    togglingRef.current = true;
    const nextLiked = !isLiked;
    setIsLiked(nextLiked);
    setCount((c) => (nextLiked ? c + 1 : Math.max(0, c - 1)));
    try {
      await onToggle(nextLiked);
    } catch (e) {
      // rollback on failure
      setIsLiked(isLiked);
      setCount((c) => (isLiked ? c + 1 : Math.max(0, c - 1)));
    } finally {
      togglingRef.current = false;
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <IconButton onClick={toggleLike} color="primary">
        {isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
      </IconButton>
      <Typography variant="body2">{likes.toLocaleString()} likes</Typography>
    </Stack>
  );
};

// --- Comment Form ---
const CommentForm: React.FC<{ onSubmit: (c: Comment) => void }> = ({
  onSubmit,
}) => {
  const [text, setText] = useState("");
  const customerData = useAppSelector(
    (state) => state.customer.personalDetails
  );
  const usermaster_id = sessionStorage.getItem("user_id");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSubmit({
      user_id: Number(sessionStorage.getItem("user_id")) || 0,
      first_name: customerData?.firstname ?? "GuestUser",
      vid_comment: text.trim(),
    });
    setText("");
  };

  return (
    <Box component="form" onSubmit={handleSubmit} mt={3}>
      <Stack direction="row" spacing={2}>
        {/* <Avatar src="https://placehold.co/40x40/7c3aed/ffffff?text=G" /> */}
        <CommentAvatar
          first_name={customerData?.firstname}
          user_id={Number(usermaster_id)}
        />
        <TextField
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          label="Add a comment"
          multiline
          rows={2}
        />
      </Stack>
      <Box mt={2} textAlign="right">
        <Button type="submit" variant="contained" disabled={!text.trim()}>
          Comment
        </Button>
      </Box>
    </Box>
  );
};

const CommentAvatar: React.FC<{ first_name?: string; user_id: number }> = ({
  first_name,
  user_id,
}) => {
  const bg = useMemo(() => {
    // simple random pastel
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 60%, 70%)`;
  }, []); // empty deps -> stable for this instance

  return (
    <Avatar sx={{ bgcolor: bg, color: "#000" }}>
      {first_name ? first_name[0].toUpperCase() : "G"}
    </Avatar>
  );
};

// --- Comment List ---
const CommentList: React.FC<{ comments: Comment[] }> = ({ comments }) => (
  <Box mt={3}>
    {comments.map((c, i) => (
      <Box key={i} mb={2}>
        <Stack direction="row" spacing={2}>
          <CommentAvatar first_name={c.first_name} user_id={c.user_id} />
          <Box>
            <Typography fontWeight="bold">
              {" "}
              {c.first_name || "GuestUser"}
            </Typography>
            <Typography>{c.vid_comment}</Typography>
            {c.updated_on && (
              <Typography variant="caption" color="text.secondary">
                {new Date(c.updated_on).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Stack>
        {i !== comments.length - 1 && <Divider sx={{ my: 2 }} />}
      </Box>
    ))}
  </Box>
);

// --- Comments Section ---
interface CommentsSectionProps {
  comments: Comment[];
  onAddComment: (c: Comment) => void;
}
export const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  onAddComment,
}) => {
  // adjust this if your comment items are taller/shorter
  const COMMENT_ITEM_HEIGHT = 72;
  const VISIBLE_COUNT = 5;

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" mb={2}>
        {comments.length} Comments
      </Typography>
      <CommentForm onSubmit={onAddComment} />
      <Box
        sx={{
          maxHeight: COMMENT_ITEM_HEIGHT * VISIBLE_COUNT,
          overflowY: "auto",
          // optional: add a thin divider/shadow to indicate scroll
          position: "relative",
          marginTop: 1,
        }}
      >
        <CommentList comments={comments} />
      </Box>
    </Paper>
  );
};

// --- Main App ---
const VideoApp: React.FC = () => {
  const linkToDisplay = useAppSelector(
    (state) => state.searchReducer.linkToDisplay
  );

  const customerDetails = useAppSelector(
    (state) => state.customer.customerDetails
  );

  const videoData = {
    url: "https://youtu.be/yiFxruo4Stc", // can also be "https://www.youtube.com/watch?v=yiFxruo4Stc"
    title: "Adult Hematolymphoid - Case 2 - JIPMER - Low Grade B Cell Lymphoma",
    description: `Speaker: Dr. Prasanth\nOrganization: JIPMER\n\n55 year old female with renal involvement low-grade B Cell lymphoma.`,
    likes: 56000,
    initialComments: [
      {
        user: "MedicalStudent",
        text: "Very informative case study. Thank you for sharing!",
        avatar: "https://placehold.co/40x40/f87171/ffffff?text=M",
      },
      {
        user: "PathologyPro",
        text: "Excellent presentation by Dr. Prasanth. The renal involvement aspect is particularly interesting.",
        avatar: "https://placehold.co/40x40/34d399/ffffff?text=P",
      },
    ],
  };

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { title, article_id } = location.state as {
    article_id: string;
    title: string;
  };

  const articleId = searchParams.get("articleId") ?? article_id;
  const customer_id = sessionStorage.getItem("informaticscustomer_id");
  const usermaster_id = sessionStorage.getItem("user_id");
  const consortia_id = customerDetails?.consortiamaster_id ?? null;
  const [addComments, setAddcomments] = useState([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isLiked, setIsLiked] = useState<boolean>(false);

  const buttonLink = useMemo(() => {
    if (!articleId || !Array.isArray(linkToDisplay)) return "";
    const found = linkToDisplay.find((obj) => obj?.article_id === articleId);
    return found?.links?.[0] || "";
  }, [articleId, linkToDisplay]);

  const videoUrlToUse = buttonLink && buttonLink;

  const normalizeComments = (raw: any[]): Comment[] =>
    raw.map((c) => ({
      comment_id: c.comment_id,
      user_id: Number(c.user_id) || 0,
      first_name: c.first_name || "GuestUser",
      vid_comment: c.vid_comment || "",
      updated_on: c.updated_on,
    }));

  useEffect(() => {
    if (!articleId) return;
    fetchLikeInfo();
    fetchInitialComments();
  }, [articleId, customer_id, usermaster_id, consortia_id]);

  async function postLikeUsage(liked: boolean) {
    if (!articleId) return;
    const apiFormData = {
      data: [
        {
          customer_id: customer_id,
          usermaster_id: usermaster_id,
          consortia_id: consortia_id,
          article_id: articleId,
          is_liked: liked,
        },
      ],
    };
    const formData = new FormData();
    formData.append("detailed_json", JSON.stringify(apiFormData));
    try {
      await axios.post(
        api.baseApi + `/api/insert_video_likes_usage/insert_video_likes_usage`,
        formData
      );
    } catch (err) {
      console.log("postLikeUsage error:", err);
      throw err;
    }
  }

  async function fetchLikeInfo() {
    if (!articleId) return;
    try {
      const response = await axios.get(
        api.baseApi +
          `/api/get_article_like_by_id/get_article_like_by_id?article_id=${articleId}`
      );
      const data = response.data?.data?.[0];
      if (data) {
        setLikeCount(Number(data.liked_count ?? likeCount));
        // if backend returns whether current user has liked, set isLiked accordingly:
        if (typeof data.is_liked === "boolean") setIsLiked(data.is_liked);
      }
    } catch (err) {
      console.log("fetchLikeInfo error:", err);
    }
  }

  async function fetchInitialComments() {
    if (!articleId) return;
    try {
      const response = await axios.get(
        api.baseApi +
          `/api/get_article_comments_by_id/get_article_comments_by_id?article_id=${articleId}`
      );
      const fetched = response.data?.data?.[0];
      if (Array.isArray(fetched) && fetched.length > 0) {
        setComments(normalizeComments(fetched));
      } else {
        setComments([]);
      }
    } catch (err) {
      console.log("fetchInitialComments error:", err);
    }
  }

  async function postCommentUsage(comment: Comment) {
    if (!articleId) return;
    const apiFormData = {
      data: [
        {
          customer_id: customer_id,
          consortia_id: consortia_id,
          usermaster_id: usermaster_id,
          article_id: articleId,
          status: true,
          comments: comment.vid_comment ?? "",
          user_id: comment.user_id,
        },
      ],
    };
    const formData = new FormData();
    formData.append("detailed_json", JSON.stringify(apiFormData));
    try {
      await axios.post(
        api.baseApi +
          `/api/insert_video_coment_usage/insert_video_coment_usage?`,
        formData
      );
    } catch (err) {
      console.log("postCommentUsage error:", err);
      throw err;
    }
  }

  const handleAddComment = async (newComment: Comment) => {
    // Optimistic UI
    setComments((prev: any) => [newComment, ...prev]);
    try {
      await postCommentUsage(newComment);
      // Optionally refetch or merge if backend returns a canonical comment object
    } catch (e) {
      console.log("handleAddComment error:", e);
    }
  };

  const handleLikeToggle = async (nextLiked: boolean) => {
    // Update backend
    await postLikeUsage(nextLiked);
    // Optionally refresh total from server or keep optimistic
    setIsLiked(nextLiked);
    setLikeCount((prev) => (nextLiked ? prev + 1 : Math.max(0, prev - 1)));
  };

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", py: 5 }}>
      <Container maxWidth="lg">
        <Stack spacing={4} direction={{ xs: "column", lg: "row" }}>
          <Box flex={2}>
            <VideoPlayer videoUrl={videoUrlToUse} />
            <Typography variant="h4" mt={4}>
              {title}
            </Typography>
            <Typography mt={1} whiteSpace="pre-line">
              {videoData.description}
            </Typography>
            <LikeButton
              likes={likeCount}
              isInitiallyLiked={isLiked}
              onToggle={handleLikeToggle}
            />
          </Box>

          <Box flex={1}>
            <CommentsSection
              comments={comments ?? []}
              onAddComment={handleAddComment}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default VideoApp;
